<template>
    <section class="tours-cta">
        <div class="tours-cta-inner">
            <img
                class="tours-cta-image"
                src="/img/more-from/other-tours.jpg"
                alt=""
                loading="lazy"
                decoding="async"
            />
            <div class="tours-cta-copy">
                <SvgsMainLogo class="tours-cta-logo" aria-hidden="true" />
                <p class="tours-cta-headline">Year-Round City Tours</p>
                <p class="tours-cta-subhead">Docent-led boat, bus &amp; walking tours</p>
            </div>
            <a
                class="tours-cta-button"
                :href="link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View all Chicago Architecture Center city tours"
            >
                View All Tours
            </a>
        </div>
    </section>
</template>

<script setup>
    defineProps({
        link: {
            type: String,
            required: false,
            // Same destination as the "Other City Tours" card in more-from-carousel.
            default: 'https://www.architecture.org/city-tours',
        },
    });
</script>

<style lang="scss" scoped>
    .tours-cta {
        background-color: $red;

        // Full-bleed: the block sits inside containers capped at $hd, but the band
        // runs edge to edge. Parents are centered (margin: auto), so 50% - 50vw
        // resolves to the exact distance back out to the viewport edge.
        width: 100vw;
        margin-left: calc(50% - 50vw);

        padding: 25px 16px;

        @media (min-width: $tablet) {
            padding: 32px 16px;
        }

        // As the last block on the page, the layout reserves $bottom (96px)
        // beneath it to clear the fixed bottom nav ($bottom_height, 80px) —
        // which showed as a 16px strip of white between the red band and the
        // nav. Absorb the spacer so the band reaches the very bottom of the
        // page, and carry the nav's height as padding so this block's own
        // content still clears it. The extra 1px covers subpixel rounding at
        // fractional zoom, so no hairline shows where the two meet; and if the
        // nav is hidden, the band is simply flush with the bottom of the page.
        //
        // Default layout only: the iframe layout renders no bottom nav and
        // zeroes this spacer past $tablet, so there would be nothing to absorb.
        // The negative margin and nav-height padding that used to live here are
        // gone with the bottom navigation itself (Asana 1217333501985924). They
        // existed to absorb #main's $bottom spacer and to keep this block's
        // content clear of the fixed nav; #main no longer carries that spacer,
        // so the negative margin would now pull the band up over the footer
        // instead of sitting flush beneath the content.
    }

    .tours-cta-inner {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        column-gap: 13px;
        // Tight: on mobile the button wraps to its own row and sits close under
        // the copy, per the comp (band is 166px tall there).
        row-gap: 4px;

        max-width: $hd;
        margin: 0 auto;

        @media (min-width: $tablet) {
            flex-wrap: nowrap;
            column-gap: 37px;
        }
    }

    .tours-cta-image {
        flex: 0 0 79px;
        width: 79px;
        height: 79px;
        object-fit: cover;
        align-self: flex-start;

        @media (min-width: $tablet) {
            flex: 0 0 313px;
            width: 313px;
            height: 223px;
            align-self: center;
        }
    }

    .tours-cta-copy {
        // min-width: 0 lets the subhead wrap instead of forcing the row wider.
        flex: 1 1 180px;
        min-width: 0;
    }

    .tours-cta-logo {
        display: block;
        width: 138px;
        height: auto;

        // main-logo.vue ships hardcoded black fills plus one red "C"; on the red
        // band the whole lockup is white.
        :deep(path) {
            fill: $white;
        }

        @media (min-width: $tablet) {
            width: 243px;
        }
    }

    .tours-cta-headline {
        margin-top: 8px;

        font-family: $title;
        font-size: 16px;
        font-weight: $bold;
        line-height: 1.05;
        text-transform: uppercase;
        color: $white;

        @media (min-width: $tablet) {
            margin-top: 14px;
            font-size: 28px;
        }
    }

    .tours-cta-subhead {
        margin-top: 10px;

        font-family: $body;
        font-size: 15px;
        font-weight: $book;
        line-height: 1.2;
        color: $white;

        @media (min-width: $tablet) {
            margin-top: 16px;
            font-size: 20px;
        }
    }

    .tours-cta-button {
        // Wraps to its own row on mobile; margin-left auto right-aligns it there
        // and pushes it to the far edge of the row on desktop.
        margin-left: auto;
        flex: 0 0 auto;

        display: inline-flex;
        align-items: center;
        justify-content: center;

        padding: 11px 20px;
        border: 1px solid $white;

        font-family: $body;
        font-size: 14px;
        // $medium (500) read too light against the mockup; $bold (700) matches it
        // and the "Year-Round City Tours" heading above (Asana O2-16).
        font-weight: $bold;
        line-height: 1;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: $white;

        transition: background-color 0.1s linear, color 0.1s linear;

        &:hover,
        &:focus-visible {
            background-color: $white;
            color: $red;
        }

        @media (min-width: $tablet) {
            min-width: 280px;
            min-height: 65px;
            padding: 20px 32px;
            font-size: 18px;
        }
    }
</style>
